import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import TextareaAutosize from 'react-autosize-textarea';
import AsyncSelect from 'react-select/async';
import FilterTag from '../common/FilterTag';
import ProjectSummary from '../common/ProjectSummary';
import LocationAutocomplete from '../common/LocationAutocomplete';
import ProjectInvitationCustomTextbox from './ProjectInvitationCustomTextbox';
import {
  sanitizeResponsibilityHtml,
  responsibilitiesToListElements,
  sanitizeOnChangeValue,
  listElementsToArray,
} from '../../responsibilitiesUtils';
import ContentEditableComponent from '../common/ContentEditableComponent';
import SearchIcon from '../../../assets/images/search-icon.svg';
import DropdownIndicator from '../common/DropdownIndicator';
import customStyles from '../../dropdownStyleUtils';
import { analyticsTrack } from '../../analyticsUtils';
import { postRequest } from '../../requestUtils';
import { ABOUT_TEXT_SUGGESTION } from '../../constants';

const boldIf = (str, cond) => (cond ? <strong>{str}</strong> : str);
const firstName = (consultant) => (consultant.name ? consultant.name.split(' ')[0] : consultant.email);
const numericKeyFilter = (e) => {
  const re = /[0-9]/;
  if (e.keyCode === 8 || e.keyCode === 46 || e.keyCode === 37 || e.keyCode === 38
    || e.keyCode === 39 || e.keyCode === 40) {
    return;
  }
  if (!re.test(e.key)) {
    e.preventDefault();
  }
};

const getErrorMessages = (project) => {
  const errorMessages = [];
  const required = {
    name: 'Project name',
    description: 'Description',
    work_location: 'Work location',
    duration_range: 'Length of engagement',
    avg_time_required_range: 'Average hours per week',
    budget_minimum: 'Min hourly rate',
    budget_maximum: 'Max hourly rate',
  };
  Object.entries(required).forEach((x) => {
    if (project[x[0]] === undefined) {
      errorMessages.push(x[1]);
    }
  });

  const workLocation = project.work_location;
  if (!workLocation) {
    errorMessages.push('Work Location');
  }

  if (['hybrid', 'office'].includes(workLocation)) {
    if (!project.office_location && !project.days_in_office && !project.office_location_required) {
      errorMessages.push('Office location');
    }
  }

  if (project.responsibility_areas_attributes
    && project.responsibility_areas_attributes.length < 100) {
    errorMessages.push('Responsibilites');
  }

  return errorMessages;
};

const processRA = (project, raInput) => {
  if (project[raInput]) {
    return responsibilitiesToListElements(project[raInput]);
  }
  return '<li></li>';
};

const initialProjectName = (filters) => {
  if (!(filters.area_of_expertises)) return null;
  let name = `${filters.area_of_expertises[0]} Consultant`;

  if (filters.therapeutic_areas || filters.therapies) {
    const tags = [...(filters.therapeutic_areas || []), ...(filters.therapies || [])].join(', ');
    name = `${name} for ${tags}`;
  }
  return name;
};

class SearchProjectCreation extends Component {
  constructor(props) {
    super(props);

    const initialProject = props.project || {};

    this.state = {
      currentPage: props.project ? 3 : 1,
      project: {
        ...initialProject,
        name: initialProject.name || initialProjectName(props.filters),
        description: initialProject.description || ABOUT_TEXT_SUGGESTION,
        higher_level_role_names: props.filters.higher_level_roles,
        area_of_expertise_names: props.filters.area_of_expertises,
        therapeutic_area_names: props.filters.therapeutic_areas,
        disease_code_names: props.filters.diseases,
        therapy_names: props.filters.therapies,
        development_phase_names: props.filters.development_phases,
        degree_names: props.filters.degrees,
        skill_names: props.filters.skills,
        level_names: props.filters.levels,
        responsibility_areas_attributes: processRA(initialProject, 'responsibility_areas_attributes'),
        rate_negotiable: initialProject.rate_negotiable !== undefined
          ? initialProject.rate_negotiable : false,
      },
      suggestionShow: !initialProject.description,
      errorMessages: getErrorMessages(initialProject),
      customMessage: '',
    };

    this.setPage = this.setPage.bind(this);
    this.handleNestedAttributeChange = this.handleNestedAttributeChange.bind(this);
    this.handleSaveProjectAndInvite = this.handleSaveProjectAndInvite.bind(this);
    this.afterCreateProjectRequest = this.afterCreateProjectRequest.bind(this);
    this.afterCreateOpenMarketplaceInvite = this.afterCreateOpenMarketplaceInvite.bind(this);
    this.handleUpdateCustomMessage = this.handleUpdateCustomMessage.bind(this);
    this.handleListChange = this.handleListChange.bind(this);
  }

  setPage(page) {
    const { project } = this.state;
    const errorMessages = getErrorMessages(project);
    this.setState({ errorMessages, currentPage: page });
  }

  handleChange(value, key) {
    const { project } = this.state;
    let updatedProject;
    if (key === 'travel_time') {
      updatedProject = { ...project, [key]: value, days_of_travel: null };
    } else if (key === 'days_of_travel') {
      updatedProject = { ...project, [key]: parseInt(value, 10), travel_time: null };
    } else if (key === 'travel_location') {
      updatedProject = {
        ...project,
        project_travel_locations_attributes: [{ description: value }],
        travel_time: 'travel',
      };
    } else {
      updatedProject = { ...project, [key]: value };
    }

    this.updateProjectAndValidate(updatedProject);
  }

  handleListChange(event, inputName) {
    const { project } = this.state;

    const original = String(event.target.value);
    let value = sanitizeOnChangeValue(original);
    const newLength = value.length;
    const characterCode = value.substr(1).charCodeAt(0);
    if (newLength > 0) {
      if (characterCode === 10) {
        value = `<l>${value}</l>`;
      } else if (newLength === 1) {
        value = `<li>${value}</li>`;
      }
    }
    this.updateProjectAndValidate({ ...project, [inputName]: value });
  }

  handleNestedAttributeChange(values, key) {
    const { project } = this.state;
    let processed = values.map((value) => ({ description: value }));
    processed = processed.filter((v) => v.description !== '');
    const updatedProject = { ...project, [key]: processed };

    this.updateProjectAndValidate(updatedProject);
  }

  doesPageHaveErrors(page) {
    const { errorMessages } = this.state;
    let possibleErrorsOnPage;

    switch (page) {
      case 1:
        possibleErrorsOnPage = ['Project name', 'Description', 'Responsibilities'];
        break;
      case 2:
        possibleErrorsOnPage = ['Office location', 'Work Location'];
        break;
      case 3:
        possibleErrorsOnPage = ['Length of engagement', 'Average hours per week', 'Min hourly rate', 'Max hourly rate'];
        break;
      default:
        possibleErrorsOnPage = [];
    }

    // returns true if some of the possible errors
    // on this page are in the current error messages state variable
    return possibleErrorsOnPage.some((possibleError) => errorMessages.includes(possibleError));
  }

  updateProjectAndValidate(newProject) {
    const errorMessages = getErrorMessages(newProject);

    this.setState({ errorMessages, project: newProject });
  }

  afterCreateOpenMarketplaceInvite(data) {
    const {
      csrfToken, consultant, handleInvited, query,
    } = this.props;
    const { project, customMessage } = this.state;

    const projectResourceId = data.resource_id;
    const updatedProject = { ...project, resource_id: projectResourceId };

    this.setState({ project: updatedProject });
    postRequest(
      `/projects/${projectResourceId}/project_invitations`,
      {
        consultant_resource_id: consultant.resource_id,
        query_id: query,
        custom_message: customMessage,
        request_proposal: true,
      },
      csrfToken,
    ).then(() => {
      analyticsTrack('Clicked Send on Create New Project modal', { location: 'Consultant Profile Page' });
      handleInvited(projectResourceId);
    });
  }

  afterCreateProjectRequest(data) {
    const { handleInvited } = this.props;
    const { project } = this.state;

    const projectResourceId = data.resource_id;
    const updatedProject = { ...project, resource_id: projectResourceId };

    this.setState({ project: updatedProject });
    analyticsTrack('Clicked Send on Create New Project modal', { location: 'Hire Again Page' });
    handleInvited(projectResourceId);
  }

  handleSaveProjectAndInvite() {
    const {
      csrfToken, consultant, isRep, companyId, query, hireAgain,
    } = this.props;
    const { project } = this.state;

    const params = {
      project: {
        ...project,
        responsibility_areas_attributes:
          sanitizeResponsibilityHtml(project.responsibility_areas_attributes),
      },
    };

    if (hireAgain) {
      params.project.rehire_user_resource_id = consultant.resource_id;
      if (isRep) {
        params.project.company_id = companyId;
      }
    } else {
      params.project.ssp = true;
      params.query_id = query;
    }

    const callback = hireAgain
      ? this.afterCreateProjectRequest
      : this.afterCreateOpenMarketplaceInvite;

    postRequest('/projects', params, csrfToken).then(({ data }) => {
      callback(data);
    });
  }

  handleUpdateCustomMessage(customMessage) {
    this.setState({ customMessage });
  }

  renderPageNav() {
    const { currentPage } = this.state;
    return (
      <div className="question-tabs">
        <div
          role="button"
          tabIndex={0}
          onClick={() => this.setPage(1)}
          className={`tab ${currentPage === 1 ? 'active' : ''} ${currentPage === 2 || currentPage === 3 ? 'done' : ''}
          ${this.doesPageHaveErrors(1) ? 'incomplete' : 'complete'}`}
        >
          <div className="bar" />
          {boldIf('About', currentPage === 1)}
        </div>
        <div
          role="button"
          tabIndex={0}
          onClick={() => this.setPage(2)}
          className={`tab ${currentPage === 2 ? 'active' : ''} ${currentPage === 3 ? 'done' : ''}
          ${this.doesPageHaveErrors(2) ? 'incomplete' : 'complete'}`}
        >
          <div className="bar" />
          {boldIf('Location', currentPage === 2)}
        </div>
        <div
          role="button"
          tabIndex={0}
          onClick={() => this.setPage(3)}
          className={`tab ${currentPage === 3 ? 'active' : ''}
          ${this.doesPageHaveErrors(3) ? 'incomplete' : 'complete'}`}
        >
          <div className="bar" />
          {boldIf('Availability', currentPage === 3)}
        </div>
      </div>
    );
  }

  renderTravelQuestion() {
    const { project } = this.state;
    return (
      <div className="logistics">
        <div className="logistics-header">
          <h4>Travel Requirements</h4>
        </div>
        <div className="travel-location">
          <input
            className="travel-location-input"
            type="text"
            id="travel-location-input"
            name="travel-location-input"
            value={project.project_travel_locations_attributes
              ? project.project_travel_locations_attributes[0].description : ''}
            onChange={(e) => this.handleChange(e.target.value, 'travel_location')}
            placeholder="Ex: 40% travel to clinical sites in the East Coast"
          />
        </div>
      </div>
    );
  }

  renderWorkplaceChoice() {
    const { workLocationOptions } = this.props;
    const { project } = this.state;

    const options = Object.entries(workLocationOptions).map((location) => (
      { value: location[0], title: location[1] }));

    return (
      <div className="workplace-type-container">
        <label htmlFor="work-location">
          <h4 className="regular-spacing">
            Workplace Type
            <span className="required-text-gray">* Required</span>
          </h4>
        </label>
        <FilterTag
          options={options}
          handleChange={(option) => this.handleChange(option, 'work_location')}
          selected={project.work_location}
        />
      </div>
    );
  }

  renderQuestions() {
    const {
      currentPage, project, errorMessages, suggestionShow,
    } = this.state;
    if (currentPage === 1) {
      return (
        <div className="questions">
          <div className="form-group question name">
            <label htmlFor="name" className="required">
              <h4 className="regular-spacing">
                Project Title:
              </h4>
            </label>
            <input
              type="text"
              id="name"
              className="form-control"
              name="name"
              placeholder="e.g. Clinical Project Manager for Phase I Oncology Study"
              value={project.name}
              onChange={(e) => this.handleChange(e.target.value, 'name')}
            />
          </div>
          <div className="form-group question description">
            <div className="mb-8">
              <label htmlFor="description" className="required">
                <h4 className="regular-spacing">
                  About the project:
                </h4>
              </label>
              <p className="explanation secondary-body-text">
                Consultants tell us this is one of the first
                things they look at in a project. Details here will generate more
                positive interest in your project.
              </p>
            </div>
            <TextareaAutosize
              className="form-control form-control--light"
              name="description"
              id="description"
              rows={3}
              placeholder="e.g. We are a clinical-stage company looking for a Clinical
              Project Manager consultant to lead our Phase I study. This consultant will
              provide CRO and vendor management support, including document management and
              planning, for an ongoing Phase I Solid Tumor trial."
              onChange={(e) => this.handleChange(e.target.value, 'description')}
              value={project.description}
            />
            {suggestionShow
              && (
              <div className="clear-suggestion-container">
                <span className="suggestion-text">
                  {'We\'ve added copy suggestions'}
                </span>
                <a
                  role="button"
                  tabIndex={0}
                  onClick={() => this.handleChange('', 'description')}
                >
                  Clear Suggestions
                </a>
              </div>
              )}
          </div>
          <div className="form-group question responsibility_areas_attributes">
            <ContentEditableComponent
              title="Main responsibilities:"
              description={
                `Example: Manage and direct all clinical processes and
                operations relating to clinical data management for clinical trials.`
              }
              className="responsibilities"
              handleOnChange={(event) => this.handleListChange(event, 'responsibility_areas_attributes')}
              handleClearSuggestions={() => this.handleResponsibilitiesChange('')}
              value={project.responsibility_areas_attributes}
              errorMessage={errorMessages.includes('Responsibilites') ? 'should be more than 100 characters' : null}
              showError
              withBulletPoints
              required
              rehireOrProposalRequest
            />
          </div>
        </div>
      );
    } if (currentPage === 2) {
      return (
        <div className="questions">
          <div className="form-group question work-location">
            { this.renderWorkplaceChoice() }
            {project.work_location && project.work_location === 'remote' && this.renderRemoteLocationInput()}
            {project.work_location && ['hybrid', 'office'].includes(project.work_location) && this.renderOfficeLocationInput()}
            { this.renderTravelQuestion() }
          </div>
        </div>
      );
    }

    return (
      <div className="questions availability">
        {this.renderProjectLengthQuestion()}
        {this.renderProjectHoursQuestion()}
        {this.renderHourlyRate()}
      </div>
    );
  }

  renderReview() {
    const { project, errorMessages, customMessage } = this.state;
    const { consultant } = this.props;

    return (
      <div className="review-and-invite">
        {this.renderHeader('review')}
        <ProjectSummary
          name={project.name}
          description={project.description || 'Project description goes here...'}
          duration={project.duration_range}
          workLocation={project.work_location}
          daysOfTravel={project.days_of_travel}
          travelTime={project.travel_time}
          workCommitment={project.avg_time_required_range}
          startDate={project.approximate_start_date_local}
          minHourlyRate={project.budget_minimum}
          maxHourlyRate={project.budget_maximum}
          responsibilities={listElementsToArray(project.responsibility_areas_attributes)}
          shortOfficeLocation={project.office_location}
          travelLocations={project.project_travel_locations_attributes
            && project.project_travel_locations_attributes.map((r) => r.description)}
          tags={[]}
          areaOfExpertises={[]}
          location="SearchProjectCreation"
        />
        <ProjectInvitationCustomTextbox
          consultantFirstName={firstName(consultant)}
          updateParentMessage={this.handleUpdateCustomMessage}
          customMessage={customMessage}
        />
        {errorMessages.length > 0
          && (
          <div className="error-message">
            <i className="fa fa-exclamation-triangle" />
            Required:
            {' '}
            {errorMessages.join(', ')}
          </div>
          )}
        <div className="actions mt-40">
          <button
            className="btn btn-secondary standard-button half-spacing back-btn"
            onClick={() => {
              analyticsTrack('Clicked Edit on Create New Project modal', { location: 'Consultant Profile Page' });
              this.setPage(1);
            }}
          >
            Edit
          </button>
          <button
            className="btn btn-primary standard-button half-spacing review-and-send"
            onClick={() => this.handleSaveProjectAndInvite()}
            disabled={errorMessages.length > 0}
          >
            Send Project
          </button>
        </div>
      </div>
    );
  }

  renderActions() {
    const { currentPage } = this.state;
    const { backToProjectSelection } = this.props;

    const pageHasErrors = this.doesPageHaveErrors(currentPage);

    if (currentPage <= 1) {
      return (
        <div className="actions">
          <button
            className="btn btn-secondary standard-button half-spacing back-btn"
            onClick={() => backToProjectSelection()}
          >
            Back
          </button>
          <button
            className="btn btn-primary standard-button half-spacing next-btn"
            onClick={() => this.setPage(2)}
            disabled={pageHasErrors}
          >
            Next
          </button>
        </div>
      );
    } if (currentPage === 2) {
      return (
        <div className="actions">
          <button
            className="btn btn-secondary standard-button half-spacing back-btn"
            onClick={() => this.setPage(1)}
          >
            Back
          </button>
          <button
            className="btn btn-primary standard-button half-spacing next-btn"
            onClick={() => this.setPage(3)}
            disabled={pageHasErrors}
          >
            Next
          </button>
        </div>
      );
    }
    return (
      <div className="actions">
        <button
          className="btn btn-secondary standard-button half-spacing back-btn"
          onClick={() => this.setPage(2)}
        >
          Back
        </button>
        <button
          className="btn btn-primary standard-button half-spacing next-btn"
          onClick={() => this.setPage(4)}
          disabled={pageHasErrors}
        >
          Review & Send
        </button>
      </div>
    );
  }

  renderHeader(context) {
    const { hireAgain, consultant } = this.props;

    if (hireAgain) {
      const hireAgainHeaderCopy = context === 'review'
        ? `Review project information. ${firstName(consultant)} will read it.`
        : `${consultant.name} will read this project information.`;

      const subtitle = `Clora will share this information with ${firstName(consultant)} and 
      check their availability.`;

      return (
        <div className="pt-60 mb-4">
          <h1 className="mb-12">
            {hireAgainHeaderCopy}
          </h1>
          <div className="body-text-2 normal-weight">
            {subtitle}
          </div>
        </div>
      );
    }
    return null;
  }

  renderOfficeLocationInput() {
    const { project } = this.state;
    return (
      <Fragment>
        <div>
          <div className="logistics-header">
            <h4>
              Days in the Office
              <span className="required-text-gray">* Required</span>
            </h4>
          </div>
          <div className="office-days-container">
            <input
              className="office-days-input"
              placeholder="5"
              type="number"
              id="office-days"
              name="office-days"
              onChange={(e) => this.handleChange(e.target.value, 'days_in_office')}
              value={project.days_in_office}
            />
            <span className="office-days-text">
              days per week in the office.
            </span>
          </div>
        </div>
        <div>
          <div className="logistics-header">
            <h4>
              Office Location
              <span className="required-text-gray">* Required</span>
            </h4>
          </div>
          <div className="location-input">
            <LocationAutocomplete
              inputId="office-location-input"
              value={project.office_location}
              onValueChange={(value) => this.handleChange(value, 'office_location')}
              defaultSample="Ex: 123 Boston Way, Boston, MA"
            />
            <img src={SearchIcon} className="search-icon" alt="search-icon" />
          </div>
        </div>
      </Fragment>
    );
  }

  renderRemoteLocationInput() {
    const { project } = this.state;
    return (
      <div>
        <div className="logistics-header">
          <h4>Desired Candidate Location</h4>
        </div>
        <div className="remote-preferred-location">
          <input
            className="remote-location-input"
            type="text"
            id="remote-location-input"
            name="remote-location-input"
            value={project.remote_preferred_location}
            onChange={(e) => this.handleChange(e.target.value, 'remote_preferred_location')}
            placeholder="Ex: Bay area, CA; Conn, NJ, or NY"
          />
        </div>
      </div>
    );
  }

  renderHourlyRate() {
    const { project } = this.state;
    return (
      <div className="body-text-2 normal-weight mb-4">
        <div className="logistics-header">
          <h4>
            Preferred Hourly Rate
            <span className="required-text">* Required</span>
          </h4>
        </div>
        <div className="budget-container">
          <div className="budget-input-container">
            <span className="dollar-icon">$</span>
            <input
              name="budget-minimum"
              id="budget-minimum"
              className="form-control rate-form budget-input"
              placeholder="Minimum"
              required="required"
              value={project.budget_minimum ? project.budget_minimum : ''}
              onChange={(e) => this.handleChange(Number(e.target.value), 'budget_minimum')}
            />
          </div>
          <span className="mx-3 range-span">-</span>
          <div className="budget-input-container">
            <span className="dollar-icon">$</span>
            <input
              name="budget-maximum"
              id="budget-maximum"
              className="form-control rate-form budget-input"
              placeholder="Maximum"
              required="required"
              value={project.budget_maximum ? project.budget_maximum : ''}
              onChange={(e) => this.handleChange(Number(e.target.value), 'budget_maximum')}
            />
          </div>
        </div>
        <div className="rate-question">
          <label
            htmlFor="rate-negotiable-checkbox"
            className="custom-checkbox-container"
          >
            <input
              type="checkbox"
              id="rate-negotiable-checkbox"
              name="rate-negotiable-checkbox"
              className="checkbox-box"
              checked={project.rate_negotiable}
              onChange={(e) => this.handleChange(e.target.checked, 'rate_negotiable')}
            />
            <span className="checkmark" />
            <span className={`checkbox-text ${project.rate_negotiable ? 'active' : ''}`}>
              Rate is negotiable
            </span>
          </label>
        </div>
      </div>
    );
  }

  renderProjectLengthQuestion() {
    const { project } = this.state;
    return (
      <div className="project-length-question">
        <h4>
          What is the expected length of the engagement?
          <span className="heading-required">* Required</span>
        </h4>
        <div className="project-length-container">
          <input
            placeholder=""
            type="number"
            id="project-length"
            name="project-length"
            value={project.duration_range ? project.duration_range : ''}
            onKeyDown={numericKeyFilter}
            onChange={(e) => this.handleChange(e.target.value, 'duration_range')}
          />
          <span className="ms-2">months</span>
        </div>
        { project.duration_range && project.duration_range > 24
          && (
          <div className="error-text">
            Cannot be more than 24 months.
          </div>
          )}
      </div>
    );
  }

  renderProjectHoursQuestion() {
    const { project } = this.state;
    const { timePerWeekOptions } = this.props;
    return (
      <div className="project-hours-question">
        <h4>
          How many hours on average per week will the consultant work?
          <span className="heading-required">* Required</span>
        </h4>
        <div className="project-hours-container mt-2">
          <AsyncSelect
            components={{ DropdownIndicator, IndicatorSeparator: () => null }}
            classNamePrefix="clora-multiselect"
            styles={customStyles}
            name="project-hours"
            id="project-hours"
            className="project-hours-select"
            defaultOptions={timePerWeekOptions.map((item) => ({ value: item, label: `${item} hours` }))}
            placeholder="Select hours"
            value={project.avg_time_required_range ? {
              value: project.avg_time_required_range,
              label: `${project.avg_time_required_range} hours`,
            }
              : null}
            onChange={(selected) => this.handleChange(selected.value, 'avg_time_required_range')}
          />
        </div>
      </div>
    );
  }

  render() {
    const { consultant, hireAgain } = this.props;
    const { currentPage } = this.state;
    return (
      <div className="search-project-creation">
        { !hireAgain
          && (
          <div className="consultant-detail">
            <div className="init-icon">
              <span className="icon-initials">{ consultant.initials }</span>
            </div>
            <div className="body-text-2 medium-weight invite-heading">
              {`Tell ${consultant.first_name} about your project and who you’re looking for.`}
            </div>
          </div>
          )}
        {currentPage < 4
          ? (
            <div className="questions-container">
              {this.renderHeader('intro')}
              {this.renderPageNav()}
              {this.renderQuestions()}
              {this.renderActions()}
            </div>
          )
          : <Fragment>{this.renderReview()}</Fragment>}
      </div>
    );
  }
}

SearchProjectCreation.propTypes = {
  consultant: PropTypes.shape({}).isRequired,
  project: PropTypes.shape({}),
  backToProjectSelection: PropTypes.func.isRequired,
  workLocationOptions: PropTypes.shape({}).isRequired,
  timePerWeekOptions: PropTypes.arrayOf(PropTypes.string).isRequired,
  csrfToken: PropTypes.string.isRequired,
  handleInvited: PropTypes.func.isRequired,
  query: PropTypes.number,
  hireAgain: PropTypes.bool,
  companyId: PropTypes.number,
  isRep: PropTypes.bool,
  filters: PropTypes.shape({
    higher_level_roles: PropTypes.arrayOf(PropTypes.string),
    area_of_expertises: PropTypes.arrayOf(PropTypes.string),
    therapeutic_areas: PropTypes.arrayOf(PropTypes.string),
    diseases: PropTypes.arrayOf(PropTypes.string),
    therapies: PropTypes.arrayOf(PropTypes.string),
    development_phases: PropTypes.arrayOf(PropTypes.string),
    degrees: PropTypes.arrayOf(PropTypes.string),
    skills: PropTypes.arrayOf(PropTypes.string),
    levels: PropTypes.arrayOf(PropTypes.string),
  }),
};

SearchProjectCreation.defaultProps = {
  project: null,
  query: null,
  hireAgain: false,
  companyId: 0,
  filters: {},
};

export default SearchProjectCreation;
